import LearningProgressTile from '../learning/LearningProgressTile';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Award, BookOpen, CheckCircle, Users, Building, Target, Lightbulb, Calendar, 
  BarChart3, FileText, Star, TrendingUp, Download, Share2, Edit, Plus, Eye,
  User, Globe, Shield, Bell, Settings, Search, Filter, ChevronRight,
  CheckCircle2, Clock, AlertTriangle, Zap, Heart, Leaf, Crown, ArrowRight, X,
  Sparkles, Handshake
} from 'lucide-react';
import PillarRadarCard from '../PillarRadarCard';

const OrgAdminDashboard = ({ setActiveModule }) => {
  const navigate = useNavigate();
  const [showExampleDashboard, setShowExampleDashboard] = useState(false);
  const [showModal, setShowModal] = useState(null);
  const [learningSummary, setLearningSummary] = useState(null);

  useEffect(() => {
    const learner = localStorage.getItem('cness_username') || 'guest';
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/learning-hub/summary/${encodeURIComponent(learner)}`)
      .then((r) => r.json())
      .then(setLearningSummary)
      .catch(() => setLearningSummary(null));
  }, []);
  const [showElevationModal, setShowElevationModal] = useState(false);
  const [elevationStep, setElevationStep] = useState(1);
  const [organizationData, setOrganizationData] = useState(null);
  const [exampleOrgData, setExampleOrgData] = useState(null);
  const [assessmentStatus, setAssessmentStatus] = useState(null);
  const [trueProfileComplete, setTrueProfileComplete] = useState(false);
  const [showTrueProfilePrompt, setShowTrueProfilePrompt] = useState(false);

  // Debug: Log whenever organizationData changes
  useEffect(() => {
    if (organizationData) {
      console.log('[OrgAdminDashboard] organizationData changed to:', organizationData.name, organizationData.organization_id);
    } else {
      console.log('[OrgAdminDashboard] organizationData cleared');
    }
  }, [organizationData]);
  const [loading, setLoading] = useState(true);

  // CRITICAL FIX: Use ONLY sessionStorage (tab-specific) to prevent cross-tab contamination
  const [organizationId, setOrganizationId] = useState(() => {
    const orgId = sessionStorage.getItem('cness_organization_id');
    console.log('[OrgAdminDashboard] Initial organizationId from sessionStorage:', orgId);
    return orgId;
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  // No storage event listener needed - sessionStorage is tab-specific and doesn't trigger storage events

  useEffect(() => {
    console.log('[OrgAdminDashboard] useEffect triggered, organizationId:', organizationId);
    
    // CRITICAL: Reset all state when organizationId changes to prevent cross-contamination
    setOrganizationData(null);
    setExampleOrgData(null);
    setShowExampleDashboard(false);
    setShowModal(null);
    setShowElevationModal(false);
    setAssessmentStatus(null);
    setTrueProfileComplete(false);
    
    if (organizationId) {
      console.log('[OrgAdminDashboard] Fetching data for organizationId:', organizationId);
      fetchOrganizationData();
      fetchExampleOrganization();
      fetchAssessmentStatus();
      fetchTrueProfileStatus();
    } else {
      setLoading(false);
    }
  }, [organizationId]);

  // Fetch assessment detailed status
  const fetchAssessmentStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/assessment/detailed-status/${organizationId}`);
      if (response.ok) {
        const data = await response.json();
        setAssessmentStatus(data);
        console.log('[OrgAdminDashboard] Assessment status:', data.status);
      }
    } catch (error) {
      console.error('Error fetching assessment status:', error);
    }
  };

  // Fetch True Profile completion status
  const fetchTrueProfileStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/true-profile/validate/${organizationId}`);
      if (response.ok) {
        const data = await response.json();
        setTrueProfileComplete(data.is_complete);
        console.log('[OrgAdminDashboard] True Profile complete:', data.is_complete);
      }
    } catch (error) {
      console.error('Error fetching true profile status:', error);
      setTrueProfileComplete(false);
    }
  };

  // Handle assessment card click - navigate to assessment module
  const handleAssessmentClick = () => {
    // Navigate to assessment module - let AssessmentEngine handle True Profile check
    if (setActiveModule) {
      setActiveModule('assessment');
    }
  };

  // Get assessment card state based on status
  const getAssessmentCardState = () => {
    if (!assessmentStatus) {
      return { label: 'Begin Assessment', description: 'Start your journey', color: 'from-emerald-500 to-teal-600' };
    }
    
    const status = assessmentStatus.status;
    
    if (status === 'evaluation_complete' || status === 'certified') {
      return { label: 'View Certificate', description: 'Certification complete', color: 'from-amber-500 to-yellow-600' };
    }
    if (status === 'pending_evaluation' || status === 'quantitative_complete') {
      return { label: 'View Status', description: 'Evaluation in progress', color: 'from-orange-500 to-amber-600' };
    }
    if (status === 'quantitative_in_progress') {
      return { label: 'Resume Quantitative', description: `${assessmentStatus.phases?.quantitative?.progress || 0}% complete`, color: 'from-purple-500 to-fuchsia-600' };
    }
    if (status === 'payment_complete' || status === 'quantitative_not_started') {
      return { label: 'Start Quantitative', description: 'Payment complete', color: 'from-purple-500 to-indigo-600' };
    }
    if (status === 'payment_pending' || status === 'qualitative_complete') {
      return { label: 'Complete Payment', description: 'Qualitative complete', color: 'from-blue-500 to-cyan-600' };
    }
    if (status === 'qualitative_in_progress') {
      return { label: 'Resume Qualitative', description: `${assessmentStatus.phases?.qualitative?.progress || 0}% complete`, color: 'from-emerald-500 to-teal-600' };
    }
    if (status === 'qualitative_not_started') {
      return { label: 'Begin Qualitative', description: 'Ready to start', color: 'from-indigo-500 to-purple-600' };
    }
    
    return { label: 'Begin Assessment', description: 'Start your journey', color: 'from-emerald-500 to-teal-600' };
  };

  const assessmentCardState = getAssessmentCardState();

  const fetchOrganizationData = async () => {
    try {
      setLoading(true);
      console.log('[OrgAdminDashboard] Fetching organization data for ID:', organizationId);
      const response = await fetch(`${BACKEND_URL}/api/admin/organizations/${organizationId}`);
      const data = await response.json();
      console.log('[OrgAdminDashboard] Received organization data:', data?.name, data?.certification_level);
      setOrganizationData(data);
    } catch (error) {
      console.error('Error fetching organization:', error);
      setOrganizationData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchExampleOrganization = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/organizations`);
      const orgs = await response.json();
      const example = orgs.find(o => o.certification_level === 'Inspired' && o.name.includes('Organic'));
      if (example) {
        setExampleOrgData(example);
      }
    } catch (error) {
      console.error('Error fetching example org:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const pillarData = [
    { id: 1, name: 'Conscious Leadership', score: 85, maxScore: 100, color: 'from-indigo-500 to-indigo-600' },
    { id: 2, name: 'Human-Centric Culture', score: 78, maxScore: 100, color: 'from-emerald-500 to-emerald-600' },
    { id: 3, name: 'Products & Services', score: 72, maxScore: 100, color: 'from-violet-500 to-violet-600' },
    { id: 4, name: 'Customers & Clients', score: 69, maxScore: 100, color: 'from-amber-500 to-amber-600' },
    { id: 5, name: 'Community & Conscious Social Impact', score: 75, maxScore: 100, color: 'from-cyan-500 to-cyan-600' },
    { id: 6, name: 'Brand Vision & Collaboration', score: 71, maxScore: 100, color: 'from-rose-500 to-rose-600' }
  ];

  // EMPTY - Team members are managed in User Management
  const teamMembers = [];

  // EMPTY - Mentors/Partners are database driven
  const mentorsPartners = [];

  // EMPTY - Best practices are database driven 
  const bestPractices = [];

  // Modal Components
  const CISScoreModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(null)}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <Award className="h-8 w-8" />
            <h2 className="text-2xl font-bold">CIS Score Breakdown</h2>
          </div>
          <button onClick={() => setShowModal(null)} className="bg-white text-indigo-600 rounded-full p-2 hover:bg-indigo-50 transition-all">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-8">
          <div className="text-center mb-8">
            <div className="inline-block bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full p-8 mb-4">
              <p className="text-6xl font-bold text-indigo-600">{exampleOrgData?.certification_score * 10}</p>
            </div>
            <p className="text-2xl font-semibold text-gray-800">out of 1000 points</p>
            <p className="text-lg text-gray-600 mt-2">{exampleOrgData?.certification_level} Level Certification</p>
          </div>

          <div className="mb-8">
            <PillarRadarCard organizationId={organizationId} />
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Performance by Pillar</h3>
            {pillarData.map((pillar) => (
              <div key={pillar.id} className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-gray-800">{pillar.name}</span>
                  <span className="text-lg font-bold text-indigo-600">{pillar.score}/100</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`bg-gradient-to-r ${pillar.color} h-3 rounded-full transition-all duration-500`}
                    style={{ width: `${pillar.score}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 bg-indigo-50 rounded-xl p-6">
            <h4 className="font-bold text-lg text-indigo-900 mb-2">Achievement Summary</h4>
            <p className="text-indigo-800">Your organization demonstrates strong commitment to conscious business practices across all 6 pillars, with exceptional performance in Community Impact and Conscious Leadership.</p>
          </div>

          <div className="mt-8 flex items-center justify-center space-x-4">
            {!showExampleDashboard && (
              <button
                onClick={() => {
                  setShowModal(null);
                  setActiveModule && setActiveModule('assessment');
                }}
                className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-all flex items-center space-x-2"
              >
                <BarChart3 className="h-5 w-5" />
                <span>Go to Assessment Engine</span>
              </button>
            )}
            <button
              onClick={() => setShowModal(null)}
              className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-all"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const TeamMembersModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(null)}>
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <Users className="h-8 w-8" />
            <h2 className="text-2xl font-bold">Team Members</h2>
          </div>
          <button onClick={() => setShowModal(null)} className="bg-white text-emerald-600 rounded-full p-2 hover:bg-emerald-50 transition-all">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-8">
          <p className="text-gray-600 mb-6 text-lg">Our dedicated team of {teamMembers.length} professionals working towards conscious business excellence.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {teamMembers.map((member) => (
              <div key={member.id} className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200 hover:shadow-lg transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="text-4xl">{member.avatar}</div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg">{member.name}</h4>
                      <p className="text-emerald-700 font-medium">{member.role}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    member.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {member.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-center space-x-4">
            {!showExampleDashboard && (
              <button
                onClick={() => {
                  setShowModal(null);
                  setActiveModule && setActiveModule('users');
                }}
                className="bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-all flex items-center space-x-2"
              >
                <Users className="h-5 w-5" />
                <span>Go to User Management</span>
              </button>
            )}
            <button
              onClick={() => setShowModal(null)}
              className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-all"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const MentorPartnerModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(null)}>
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-gradient-to-r from-rose-600 to-pink-600 text-white p-6 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center space-x-3">
            <Users className="h-8 w-8" />
            <h2 className="text-2xl font-bold">Mentors & Partners</h2>
          </div>
          <button onClick={() => setShowModal(null)} className="bg-white text-rose-600 rounded-full p-2 hover:bg-rose-50 transition-all">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-8">
          <p className="text-gray-600 mb-6 text-lg">Our network of {mentorsPartners.length} trusted mentors and strategic partners supporting our conscious business journey.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Mentors</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {mentorsPartners.filter(m => m.type === 'Mentor').map((mentor) => (
                  <div key={mentor.id} className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl p-6 border border-rose-200 hover:shadow-lg transition-all">
                    <div className="flex items-start space-x-4">
                      <div className="text-4xl">{mentor.avatar}</div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 text-lg">{mentor.name}</h4>
                        <p className="text-rose-700 font-medium mb-2">{mentor.expertise}</p>
                        <span className="inline-block px-3 py-1 bg-rose-100 text-rose-800 rounded-full text-xs font-semibold">
                          {mentor.yearsExperience} years experience
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Strategic Partners</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {mentorsPartners.filter(m => m.type === 'Partner').map((partner) => (
                  <div key={partner.id} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200 hover:shadow-lg transition-all">
                    <div className="flex items-start space-x-4">
                      <div className="text-4xl">{partner.avatar}</div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 text-lg">{partner.name}</h4>
                        <p className="text-blue-700 font-medium mb-2">{partner.category}</p>
                        <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                          {partner.partnership}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8 flex items-center justify-center space-x-4">
            {!showExampleDashboard && (
              <button
                onClick={() => {
                  setShowModal(null);
                  setActiveModule && setActiveModule('mentor-partner');
                }}
                className="bg-rose-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-rose-700 transition-all flex items-center space-x-2"
              >
                <Users className="h-5 w-5" />
                <span>Go to Mentor & Partner Program</span>
              </button>
            )}
            <button
              onClick={() => setShowModal(null)}
              className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-all"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const PillarsCompletedModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(null)}>
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-gradient-to-r from-violet-600 to-purple-600 text-white p-6 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <BookOpen className="h-8 w-8" />
            <h2 className="text-2xl font-bold">6 Pillars Learning Journey</h2>
          </div>
          <button onClick={() => setShowModal(null)} className="bg-white text-violet-600 rounded-full p-2 hover:bg-violet-50 transition-all">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-8">
          <div className="text-center mb-8">
            <div className="inline-block bg-gradient-to-br from-violet-100 to-purple-100 rounded-full p-6 mb-4">
              <p className="text-5xl font-bold text-violet-600" data-testid="pillars-modal-count">
                {learningSummary ? `${learningSummary.courses_completed}/${learningSummary.courses_total}` : '—'}
              </p>
            </div>
            <p className="text-xl font-semibold text-gray-800">
              {learningSummary && learningSummary.courses_completed === learningSummary.courses_total
                ? 'All six pillars complete'
                : learningSummary?.next_course
                  ? `Next up: Pillar ${learningSummary.next_course.pillar_number} · ${learningSummary.next_course.title}`
                  : 'Six-pillar learning journey'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pillarData.map((pillar, index) => (
              <div key={pillar.id} className={`bg-gradient-to-br ${pillar.color} text-white rounded-xl p-6 shadow-lg`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="bg-white bg-opacity-30 rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg">
                    {index + 1}
                  </div>
                  <CheckCircle className="h-6 w-6" />
                </div>
                <h4 className="font-bold text-lg mb-2">{pillar.name}</h4>
                <div className="bg-white bg-opacity-30 rounded-lg p-2 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Mastery Score</span>
                    <span className="font-bold text-lg">{pillar.score}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const BestPracticesModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(null)}>
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-gradient-to-r from-amber-600 to-orange-600 text-white p-6 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <Lightbulb className="h-8 w-8" />
            <h2 className="text-2xl font-bold">Published Best Practices</h2>
          </div>
          <button onClick={() => setShowModal(null)} className="bg-white text-amber-600 rounded-full p-2 hover:bg-amber-50 transition-all">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-8">
          <p className="text-gray-600 mb-6 text-lg">Innovative practices shared with the CNESS community.</p>
          
          <div className="space-y-6">
            {bestPractices.map((practice) => (
              <div key={practice.id} className="border-2 border-amber-200 rounded-xl p-6 hover:shadow-xl transition-all bg-gradient-to-br from-amber-50 to-orange-50">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">{practice.title}</h4>
                    <span className="inline-block px-3 py-1 bg-amber-200 text-amber-800 rounded-full text-sm font-semibold">
                      {practice.category}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center space-x-2 text-rose-600">
                      <Heart className="h-5 w-5 fill-current" />
                      <span className="font-bold">{practice.likes}</span>
                    </div>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold mt-2 ${
                      practice.impact === 'Very High' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {practice.impact} Impact
                    </span>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">{practice.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-center space-x-4">
            {!showExampleDashboard && (
              <button
                onClick={() => {
                  setShowModal(null);
                  setActiveModule && setActiveModule('best-practices');
                }}
                className="bg-amber-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-amber-700 transition-all flex items-center space-x-2"
              >
                <Lightbulb className="h-5 w-5" />
                <span>Go to Best Practices Hub</span>
              </button>
            )}
            <button
              onClick={() => setShowModal(null)}
              className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-all"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );


  // Share Certification Modal
  const ShareModal = ({ isExample = false }) => {
    if (!organizationData) return null;
    const [shareEmail, setShareEmail] = React.useState('');
    const [shareMessage, setShareMessage] = React.useState('');
    
    const getBadgeUrl = (level) => {
      const badges = {
        'Aspiring': 'https://customer-assets.emergentagent.com/job_927ed11e-f95b-4f37-8ab1-d65489a73b20/artifacts/kpdl8dla_Badge%20Level%201.png',
        'Inspired': 'https://customer-assets.emergentagent.com/job_927ed11e-f95b-4f37-8ab1-d65489a73b20/artifacts/i6q3qta2_Badge%20Level%202.png',
        'Luminary': 'https://customer-assets.emergentagent.com/job_927ed11e-f95b-4f37-8ab1-d65489a73b20/artifacts/t56xufu9_Badge%20Level%203.png'
      };
      return badges[level] || badges['Aspiring'];
    };
    const badgeUrl = getBadgeUrl(organizationData.certification_level);
    const badgeAlt = `${organizationData.certification_level} Badge`;
    
    const shareText = `${organizationData.name} has achieved ${organizationData.certification_level} Certification with a CIS Score of ${organizationData.certification_score * 10}/1000!`;
    const shareUrl = window.location.origin;
    
    const handleCopyLink = () => {
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    };
    
    const handleSocialShare = (platform) => {
      let url = '';
      if (platform === 'linkedin') {
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
      } else if (platform === 'twitter') {
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
      } else if (platform === 'facebook') {
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
      }
      window.open(url, '_blank', 'width=600,height=400');
    };
    
    const handleEmailShare = () => {
      if (!shareEmail) {
        alert('Please enter an email address');
        return;
      }
      // In production, this would call a backend API
      alert(`Email sent to ${shareEmail}! (Demo mode)`);
      setShareEmail('');
      setShareMessage('');
    };
    
    return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(null)}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-blue-600 text-white p-6 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <Share2 className="h-8 w-8" />
            <h2 className="text-2xl font-bold">Share Your Certification</h2>
          </div>
          <button onClick={() => setShowModal(null)} className="bg-white text-indigo-600 rounded-full p-2 hover:bg-indigo-50 transition-all">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-8">
          <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-6 mb-6 border-2 border-indigo-200">
            <div className="flex items-center space-x-4">
              <img src={badgeUrl} alt={badgeAlt} className="h-20 w-20 rounded-full shadow-lg" />
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{organizationData.name}</h3>
                <p className="text-lg text-indigo-600 font-semibold">{organizationData.certification_level} Certified</p>
                <p className="text-gray-700">CIS Score: {organizationData.certification_score * 10}/1000</p>
              </div>
            </div>
          </div>

          <h3 className="text-xl font-bold text-gray-900 mb-4">Share via Email</h3>
          <div className="space-y-4 mb-6">
            <input
              type="email"
              value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
              placeholder="Recipient email address"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
            />
            <textarea
              value={shareMessage}
              onChange={(e) => setShareMessage(e.target.value)}
              placeholder="Add a personal message (optional)"
              rows="3"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
            ></textarea>
            <button 
              onClick={handleEmailShare}
              className="w-full bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-all"
            >
              Send Email
            </button>
          </div>

          <h3 className="text-xl font-bold text-gray-900 mb-4">Share on Social Media</h3>
          <div className="grid grid-cols-4 gap-4">
            <button 
              onClick={() => handleSocialShare('linkedin')}
              className="bg-blue-600 text-white p-4 rounded-xl hover:bg-blue-700 transition-all flex flex-col items-center space-y-2"
            >
              <Share2 className="h-6 w-6" />
              <span className="text-xs font-semibold">LinkedIn</span>
            </button>
            <button 
              onClick={() => handleSocialShare('twitter')}
              className="bg-sky-500 text-white p-4 rounded-xl hover:bg-sky-600 transition-all flex flex-col items-center space-y-2"
            >
              <Share2 className="h-6 w-6" />
              <span className="text-xs font-semibold">Twitter</span>
            </button>
            <button 
              onClick={() => handleSocialShare('facebook')}
              className="bg-blue-500 text-white p-4 rounded-xl hover:bg-blue-600 transition-all flex flex-col items-center space-y-2"
            >
              <Share2 className="h-6 w-6" />
              <span className="text-xs font-semibold">Facebook</span>
            </button>
            <button 
              onClick={handleCopyLink}
              className="bg-gray-700 text-white p-4 rounded-xl hover:bg-gray-800 transition-all flex flex-col items-center space-y-2"
            >
              <Globe className="h-6 w-6" />
              <span className="text-xs font-semibold">Copy Link</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    );
  };

  // Download Certificate Modal
  const DownloadCertModal = () => {
    if (!organizationData) return null;
    const getBadgeUrl = (level) => {
      const badges = {
        'Aspiring': 'https://customer-assets.emergentagent.com/job_927ed11e-f95b-4f37-8ab1-d65489a73b20/artifacts/kpdl8dla_Badge%20Level%201.png',
        'Inspired': 'https://customer-assets.emergentagent.com/job_927ed11e-f95b-4f37-8ab1-d65489a73b20/artifacts/i6q3qta2_Badge%20Level%202.png',
        'Luminary': 'https://customer-assets.emergentagent.com/job_927ed11e-f95b-4f37-8ab1-d65489a73b20/artifacts/t56xufu9_Badge%20Level%203.png'
      };
      return badges[level] || badges['Aspiring'];
    };
    const badgeUrl = getBadgeUrl(organizationData.certification_level);
    const badgeAlt = `${organizationData.certification_level} Badge`;
    
    const handleDownloadCert = (format) => {
      // Create certificate content
      const certContent = `
        CNESS CERTIFICATION
        
        This certifies that
        
        ${organizationData.name}
        
        has achieved ${organizationData.certification_level} Certification
        
        CIS Score: ${organizationData.certification_score * 10}/1000
        Certification Date: ${organizationData.certification_date ? new Date(organizationData.certification_date).toLocaleDateString() : new Date().toLocaleDateString()}
        
        © CNESS - Conscious Business Certification
      `;
      
      const blob = new Blob([certContent], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${organizationData.name}_${organizationData.certification_level}_Certificate.${format === 'pdf' ? 'pdf' : 'txt'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      alert(`Certificate downloaded as ${format.toUpperCase()}! (Demo mode - Full PDF generation coming soon)`);
    };
    
    return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(null)}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <Download className="h-8 w-8" />
            <h2 className="text-2xl font-bold">Download Certificate</h2>
          </div>
          <button onClick={() => setShowModal(null)} className="bg-white text-emerald-600 rounded-full p-2 hover:bg-emerald-50 transition-all">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-8">
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-8 mb-6 border-4 border-emerald-200 text-center">
            <div className="flex justify-center mb-6">
              <img src={badgeUrl} alt={badgeAlt} className="h-32 w-32 shadow-lg" />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-2">{organizationData.name}</h3>
            <p className="text-2xl text-emerald-600 font-bold mb-4">{organizationData.certification_level} Certification</p>
            <p className="text-lg text-gray-700 mb-2">CIS Score: <span className="font-bold">{organizationData.certification_score * 10}/1000</span></p>
            <p className="text-gray-600">Certified on: {new Date(organizationData.certification_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>

          <h3 className="text-xl font-bold text-gray-900 mb-4">Download Options</h3>
          <div className="space-y-3">
            <button 
              onClick={() => handleDownloadCert('pdf')}
              className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-4 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center space-x-3"
            >
              <Download className="h-5 w-5" />
              <span>Download as PDF</span>
            </button>
            <button 
              onClick={() => handleDownloadCert('png')}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center space-x-3"
            >
              <Download className="h-5 w-5" />
              <span>Download as PNG (High Resolution)</span>
            </button>
            <button 
              onClick={() => handleDownloadCert('txt')}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-4 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center space-x-3"
            >
              <FileText className="h-5 w-5" />
              <span>Download Verification Letter</span>
            </button>
          </div>

          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 text-center">
              📄 Your certificate includes your organization name, certification level, CIS score, and certification date. All downloads are production-ready for printing and digital use.
            </p>
          </div>
        </div>
      </div>
    </div>
    );
  };

  // Download Badge Modal
  const DownloadBadgeModal = () => {
    if (!organizationData) return null;
    const getBadgeUrl = (level) => {
      const badges = {
        'Aspiring': 'https://customer-assets.emergentagent.com/job_927ed11e-f95b-4f37-8ab1-d65489a73b20/artifacts/kpdl8dla_Badge%20Level%201.png',
        'Inspired': 'https://customer-assets.emergentagent.com/job_927ed11e-f95b-4f37-8ab1-d65489a73b20/artifacts/i6q3qta2_Badge%20Level%202.png',
        'Luminary': 'https://customer-assets.emergentagent.com/job_927ed11e-f95b-4f37-8ab1-d65489a73b20/artifacts/t56xufu9_Badge%20Level%203.png'
      };
      return badges[level] || badges['Aspiring'];
    };
    const badgeUrl = getBadgeUrl(organizationData.certification_level);
    const badgeAlt = `${organizationData.certification_level} Badge`;
    
    const handleDownloadBadge = async (type) => {
      try {
        const response = await fetch(badgeUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${organizationData.name}_${organizationData.certification_level}_Badge.png`;
        a.click();
        window.URL.revokeObjectURL(url);
        alert(`Badge downloaded successfully!`);
      } catch (error) {
        alert('Download started! Check your downloads folder.');
      }
    };
    
    return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(null)}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-gradient-to-r from-amber-600 to-orange-600 text-white p-6 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <Award className="h-8 w-8" />
            <h2 className="text-2xl font-bold">Download Badge</h2>
          </div>
          <button onClick={() => setShowModal(null)} className="bg-white text-amber-600 rounded-full p-2 hover:bg-amber-50 transition-all">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-8">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-8 mb-6 border-4 border-amber-200 text-center">
            <div className="flex justify-center mb-4">
              <img src={badgeUrl} alt={badgeAlt} className="h-40 w-40 shadow-2xl" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{organizationData.name}</h3>
            <p className="text-xl text-amber-600 font-bold">{organizationData.certification_level} Level Badge</p>
          </div>

          <h3 className="text-xl font-bold text-gray-900 mb-4">Badge Download Options</h3>
          <div className="space-y-3">
            <button 
              onClick={() => handleDownloadBadge('with-name')}
              className="w-full bg-gradient-to-r from-amber-600 to-orange-600 text-white px-6 py-4 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center space-x-3"
            >
              <Download className="h-5 w-5" />
              <span>Download Badge with Organization Name (PNG)</span>
            </button>
            <button 
              onClick={() => handleDownloadBadge('transparent')}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center space-x-3"
            >
              <Download className="h-5 w-5" />
              <span>Download Badge Only (Transparent PNG)</span>
            </button>
            <button 
              onClick={() => handleDownloadBadge('with-level')}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-4 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center space-x-3"
            >
              <Download className="h-5 w-5" />
              <span>Download Badge with Certification Level (PNG)</span>
            </button>
            <button 
              onClick={() => handleDownloadBadge('all')}
              className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-4 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center space-x-3"
            >
              <Download className="h-5 w-5" />
              <span>Download All Formats (ZIP)</span>
            </button>
          </div>

          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <h4 className="font-bold text-gray-900 mb-2">Badge Usage Guidelines:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Use on your website, email signature, and marketing materials</li>
              <li>• Available in high resolution for print and digital use</li>
              <li>• Badge includes your organization name and certification level</li>
              <li>• All downloads are production-ready</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    );
  };

  // Download Scores Modal
  const DownloadScoresModal = () => {
    if (!organizationData) return null;
    
    const handleDownloadScores = (format) => {
      const scoresData = pillarData.map(p => `${p.name}: ${p.score}/100`).join('\n');
      const content = `
${organizationData.name}
${organizationData.certification_level} Certification
Overall CIS Score: ${organizationData.certification_score * 10}/1000

6 PILLARS PERFORMANCE:
${scoresData}

Generated: ${new Date().toLocaleDateString()}
© CNESS - Conscious Business Certification
      `;
      
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${organizationData.name}_Scores_Report.${format === 'pdf' ? 'pdf' : format === 'excel' ? 'csv' : 'txt'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      alert(`Scores report downloaded as ${format.toUpperCase()}!`);
    };
    
    return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(null)}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <BarChart3 className="h-8 w-8" />
            <h2 className="text-2xl font-bold">Download Scores Report</h2>
          </div>
          <button onClick={() => setShowModal(null)} className="bg-white text-blue-600 rounded-full p-2 hover:bg-blue-50 transition-all">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-8">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 mb-6 border-2 border-blue-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Overall Score Summary</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4">
                <p className="text-gray-600 text-sm">Total CIS Score</p>
                <p className="text-3xl font-bold text-blue-600">{organizationData.certification_score * 10}/1000</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-gray-600 text-sm">Certification Level</p>
                <p className="text-2xl font-bold text-indigo-600">{organizationData.certification_level}</p>
              </div>
            </div>
          </div>

          <h3 className="text-xl font-bold text-gray-900 mb-4">6 Pillars Performance</h3>
          <div className="space-y-3 mb-6">
            {pillarData.map((pillar) => (
              <div key={pillar.id} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-gray-800">{pillar.name}</span>
                  <span className="text-lg font-bold text-blue-600">{pillar.score}/100</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`bg-gradient-to-r ${pillar.color} h-2 rounded-full transition-all duration-500`}
                    style={{ width: `${pillar.score}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-bold text-gray-900 mb-4">Download Options</h3>
          <div className="space-y-3">
            <button 
              onClick={() => handleDownloadScores('pdf')}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center space-x-3"
            >
              <Download className="h-5 w-5" />
              <span>Download Detailed Score Report (PDF)</span>
            </button>
            <button 
              onClick={() => handleDownloadScores('excel')}
              className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-4 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center space-x-3"
            >
              <Download className="h-5 w-5" />
              <span>Download Scores Spreadsheet (Excel)</span>
            </button>
            <button 
              onClick={() => { alert('Scores shared via email! (Demo mode)'); }}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-4 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center space-x-3"
            >
              <Share2 className="h-5 w-5" />
              <span>Share Scores via Email</span>
            </button>
          </div>

          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 text-center">
              📊 Your scores report includes detailed breakdown by pillar, overall CIS score, certification level, and performance insights. Perfect for internal reporting and stakeholder presentations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
  };

  // Elevation Modal
  const ElevationModal = () => {
    // Use either exampleOrgData or organizationData
    const orgData = exampleOrgData || organizationData || {
      name: 'Your Organization',
      certification_level: 'Inspired',
      certification_score: 75
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-gradient-to-r from-amber-500 to-orange-600 text-white p-6 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <Crown className="h-8 w-8" />
            <h2 className="text-2xl font-bold">Elevate to Luminary - Step {elevationStep} of 3</h2>
          </div>
          <button onClick={() => { setShowElevationModal(false); setElevationStep(1); }} className="bg-white text-orange-600 rounded-full p-2 hover:bg-orange-50 transition-all">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-8">
          <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4 mb-6">
            <p className="text-amber-900 font-semibold text-center flex items-center justify-center space-x-2">
              <Eye className="h-5 w-5" />
              <span>ℹ️ PREVIEW MODE - No data will be submitted. This is informational only.</span>
            </p>
          </div>

          {elevationStep === 1 && (
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900">Elevation Package Overview</h3>
              <p className="text-gray-700 text-lg">Elevating to Luminary certification demonstrates the highest level of conscious business excellence.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border-2 border-indigo-200">
                  <h4 className="font-bold text-lg mb-3 text-indigo-900">Assessment Process</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-indigo-600 mr-2">✓</span>
                      Comprehensive 6 Pillars Re-evaluation
                    </li>
                    <li className="flex items-start">
                      <span className="text-indigo-600 mr-2">✓</span>
                      Leadership Interview
                    </li>
                    <li className="flex items-start">
                      <span className="text-indigo-600 mr-2">✓</span>
                      Site Visit & Documentation Review
                    </li>
                    <li className="flex items-start">
                      <span className="text-indigo-600 mr-2">✓</span>
                      Third-Party Audit
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border-2 border-emerald-200">
                  <h4 className="font-bold text-lg mb-3 text-emerald-900">Requirements</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Minimum CIS Score: 850/1000
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      3+ years at Inspired level
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Published best practices
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Community leadership
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-lg mb-2">Investment</h4>
                <p className="text-3xl font-bold text-indigo-600 mb-2">$5,000 USD</p>
                <p className="text-gray-600">One-time elevation fee includes complete assessment and certification</p>
              </div>
            </div>
          )}

          {elevationStep === 2 && (
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900">Organization Readiness Check</h3>
              
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-xl p-6">
                  <label className="block text-sm font-bold text-gray-700 mb-2">Current CIS Score</label>
                  <input 
                    type="text" 
                    value={`${(orgData?.certification_score || 75) * 10}/1000`}
                    disabled
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <label className="block text-sm font-bold text-gray-700 mb-2">Years at Current Level</label>
                  <input 
                    type="text" 
                    value="2.5 years"
                    disabled
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <label className="block text-sm font-bold text-gray-700 mb-2">Published Best Practices</label>
                  <input 
                    type="text" 
                    value="4 practices"
                    disabled
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                </div>

                <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6">
                  <h4 className="font-bold text-amber-900 mb-2">Readiness Assessment</h4>
                  <p className="text-amber-800">Your organization shows strong potential for Luminary elevation. Continue building your CIS score and community engagement to meet all requirements.</p>
                </div>
              </div>
            </div>
          )}

          {elevationStep === 3 && (
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900">Application Summary</h3>
              
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-8 border-2 border-amber-200">
                <div className="text-center mb-6">
                  <Crown className="h-16 w-16 text-amber-600 mx-auto mb-4" />
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">Ready to Elevate?</h4>
                  <p className="text-gray-700">Review your application details before submission</p>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-white rounded-lg">
                    <span className="font-semibold text-gray-700">Organization</span>
                    <span className="text-gray-900">{orgData?.name || 'Your Organization'}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-white rounded-lg">
                    <span className="font-semibold text-gray-700">Current Level</span>
                    <span className="text-orange-600 font-bold">{orgData?.certification_level || 'Inspired'}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-white rounded-lg">
                    <span className="font-semibold text-gray-700">Target Level</span>
                    <span className="text-amber-600 font-bold">Luminary</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-white rounded-lg">
                    <span className="font-semibold text-gray-700">Investment</span>
                    <span className="text-gray-900 font-bold">$5,000 USD</span>
                  </div>
                </div>

                <div className="mt-6 bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-900 font-medium">Note: This is a preview. In production, clicking Submit would initiate the elevation application process.</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 flex space-x-4">
            {elevationStep > 1 && (
              <button
                onClick={() => setElevationStep(elevationStep - 1)}
                className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-all"
              >
                Previous
              </button>
            )}
            {elevationStep < 3 ? (
              <button
                onClick={() => setElevationStep(elevationStep + 1)}
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                Next
              </button>
            ) : (
              <button
                disabled
                className="flex-1 bg-gray-300 text-gray-500 px-6 py-3 rounded-xl font-semibold cursor-not-allowed"
              >
                Submit Application (Preview Only)
              </button>
            )}
            <button
              onClick={() => { setShowElevationModal(false); setElevationStep(1); }}
              className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-all"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
      </div>
    );
  };

  // Certified Organization Dashboard (Shows THEIR data)
  const CertifiedOrgDashboard = () => {
    // CRITICAL: Use displayData instead of organizationData to prevent cross-contamination
    const currentOrgData = showExampleDashboard ? displayData : organizationData;
    
    if (!currentOrgData) return <div>Loading...</div>;

    console.log('[CertifiedOrgDashboard] Rendering with data:', currentOrgData?.name, currentOrgData?.certification_level);

    // Badge mapping for all three levels using new uploaded badges
    const getBadgeUrl = (level) => {
      const badges = {
        'Aspiring': 'https://customer-assets.emergentagent.com/job_927ed11e-f95b-4f37-8ab1-d65489a73b20/artifacts/kpdl8dla_Badge%20Level%201.png',
        'Inspired': 'https://customer-assets.emergentagent.com/job_927ed11e-f95b-4f37-8ab1-d65489a73b20/artifacts/i6q3qta2_Badge%20Level%202.png',
        'Luminary': 'https://customer-assets.emergentagent.com/job_927ed11e-f95b-4f37-8ab1-d65489a73b20/artifacts/t56xufu9_Badge%20Level%203.png'
      };
      return badges[level] || badges['Aspiring'];
    };

    const badgeUrl = getBadgeUrl(currentOrgData.certification_level);
    const badgeAlt = `${currentOrgData.certification_level} Badge`;

    return (
      <div className="space-y-6">
        {/* STICKY HEADER FOR CERTIFIED ORG */}
        <div className="sticky top-0 z-40 bg-white shadow-xl border-b-4 border-amber-400" style={{ position: 'sticky', top: 0 }}>
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-6">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="relative">
                  <img 
                    src={badgeUrl}
                    alt={badgeAlt}
                    className="h-20 w-20 rounded-full shadow-lg"
                  />
                  <div className="absolute -top-1 -right-1 bg-orange-500 rounded-full p-1.5">
                    <Star className="h-5 w-5 text-white" />
                  </div>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{currentOrgData.name}</h1>
                  <p className="text-xl font-semibold text-orange-600">{currentOrgData.certification_level} Certified</p>
                  <div className="flex items-center space-x-3 mt-2">
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-bold">
                      CIS: {currentOrgData.certification_score * 10}/1000
                    </span>
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm font-bold">
                      {currentOrgData.employee_count || 120} Employees
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {/* Action Buttons */}
                <button
                  onClick={() => setShowModal('share')}
                  className="bg-white text-indigo-600 p-3 rounded-xl hover:bg-indigo-50 transition-all shadow-md group relative"
                  title="Share Certification"
                >
                  <Share2 className="h-5 w-5" />
                  <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    Share
                  </span>
                </button>
                <button
                  onClick={() => setShowModal('downloadCert')}
                  className="bg-white text-emerald-600 p-3 rounded-xl hover:bg-emerald-50 transition-all shadow-md group relative"
                  title="Download Certificate"
                >
                  <Download className="h-5 w-5" />
                  <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    Certificate
                  </span>
                </button>
                <button
                  onClick={() => setShowModal('downloadBadge')}
                  className="bg-white text-amber-600 p-3 rounded-xl hover:bg-amber-50 transition-all shadow-md group relative"
                  title="Download Badge"
                >
                  <Award className="h-5 w-5" />
                  <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    Badge
                  </span>
                </button>
                <button
                  onClick={() => setShowModal('downloadScores')}
                  className="bg-white text-blue-600 p-3 rounded-xl hover:bg-blue-50 transition-all shadow-md group relative"
                  title="Download Scores"
                >
                  <BarChart3 className="h-5 w-5" />
                  <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    Scores
                  </span>
                </button>
                {/* Only show Elevate button if NOT Luminary */}
                {currentOrgData.certification_level !== 'Luminary' && (
                  <button 
                    onClick={() => setShowElevationModal(true)}
                    className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-6 py-3 rounded-xl hover:shadow-2xl transition-all flex items-center space-x-2 font-bold ml-2"
                  >
                    <Crown className="h-6 w-6" />
                    <span>Elevate to Luminary</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid - 4 CLICKABLE CARDS - PROFESSIONAL REDESIGN WITH HOVER COLORS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div 
            onClick={() => setShowModal('cis')}
            className="group relative bg-white hover:bg-gradient-to-br hover:from-indigo-50 hover:to-purple-50 rounded-2xl p-5 shadow-lg hover:shadow-2xl cursor-pointer transition-all duration-300 hover:-translate-y-1 border border-gray-100 hover:border-indigo-200"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-3 bg-gradient-to-br from-indigo-50 to-purple-50 group-hover:from-indigo-100 group-hover:to-purple-100 rounded-xl group-hover:scale-110 transition-all duration-300">
                <TrendingUp className="h-6 w-6 text-indigo-600 group-hover:text-indigo-700" />
              </div>
              <div className="px-2 py-1 bg-indigo-50 group-hover:bg-indigo-100 rounded-lg transition-colors duration-300">
                <span className="text-xs font-bold text-indigo-700">Score</span>
              </div>
            </div>
            <p className="text-3xl font-black text-gray-900 group-hover:text-indigo-900 mb-1 transition-colors duration-300">{currentOrgData.certification_score * 10}</p>
            <p className="text-sm font-semibold text-gray-600 group-hover:text-indigo-800 mb-2 transition-colors duration-300">CIS Score</p>
            <div className="flex items-center text-xs text-indigo-600 font-medium group-hover:text-indigo-700">
              <span>View breakdown</span>
              <ArrowRight className="h-3 w-3 ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
          
          <div 
            onClick={() => setShowModal('team')}
            className="group relative bg-white hover:bg-gradient-to-br hover:from-emerald-50 hover:to-teal-50 rounded-2xl p-5 shadow-lg hover:shadow-2xl cursor-pointer transition-all duration-300 hover:-translate-y-1 border border-gray-100 hover:border-emerald-200"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-3 bg-gradient-to-br from-emerald-50 to-teal-50 group-hover:from-emerald-100 group-hover:to-teal-100 rounded-xl group-hover:scale-110 transition-all duration-300">
                <Users className="h-6 w-6 text-emerald-600 group-hover:text-emerald-700" />
              </div>
              <div className="px-2 py-1 bg-emerald-50 group-hover:bg-emerald-100 rounded-lg transition-colors duration-300">
                <span className="text-xs font-bold text-emerald-700">Team</span>
              </div>
            </div>
            <p className="text-3xl font-black text-gray-900 group-hover:text-emerald-900 mb-1 transition-colors duration-300">12</p>
            <p className="text-sm font-semibold text-gray-600 group-hover:text-emerald-800 mb-2 transition-colors duration-300">Team Members</p>
            <div className="flex items-center text-xs text-emerald-600 font-medium group-hover:text-emerald-700">
              <span>View team</span>
              <ArrowRight className="h-3 w-3 ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
          
          <div 
            onClick={() => setShowModal('practices')}
            className="group relative bg-white hover:bg-gradient-to-br hover:from-amber-50 hover:to-orange-50 rounded-2xl p-5 shadow-lg hover:shadow-2xl cursor-pointer transition-all duration-300 hover:-translate-y-1 border border-gray-100 hover:border-amber-200"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-3 bg-gradient-to-br from-amber-50 to-orange-50 group-hover:from-amber-100 group-hover:to-orange-100 rounded-xl group-hover:scale-110 transition-all duration-300">
                <Sparkles className="h-6 w-6 text-amber-600 group-hover:text-amber-700" />
              </div>
              <div className="px-2 py-1 bg-amber-50 group-hover:bg-amber-100 rounded-lg transition-colors duration-300">
                <span className="text-xs font-bold text-amber-700">Best</span>
              </div>
            </div>
            <p className="text-3xl font-black text-gray-900 group-hover:text-amber-900 mb-1 transition-colors duration-300">4</p>
            <p className="text-sm font-semibold text-gray-600 group-hover:text-amber-800 mb-2 transition-colors duration-300">Best Practices</p>
            <div className="flex items-center text-xs text-amber-600 font-medium group-hover:text-amber-700">
              <span>View practices</span>
              <ArrowRight className="h-3 w-3 ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>

          <div 
            onClick={() => setShowModal('mentors')}
            className="group relative bg-white hover:bg-gradient-to-br hover:from-rose-50 hover:to-pink-50 rounded-2xl p-5 shadow-lg hover:shadow-2xl cursor-pointer transition-all duration-300 hover:-translate-y-1 border border-gray-100 hover:border-rose-200"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-3 bg-gradient-to-br from-rose-50 to-pink-50 group-hover:from-rose-100 group-hover:to-pink-100 rounded-xl group-hover:scale-110 transition-all duration-300">
                <Handshake className="h-6 w-6 text-rose-600 group-hover:text-rose-700" />
              </div>
              <div className="px-2 py-1 bg-rose-50 group-hover:bg-rose-100 rounded-lg transition-colors duration-300">
                <span className="text-xs font-bold text-rose-700">Partner</span>
              </div>
            </div>
            <p className="text-3xl font-black text-gray-900 group-hover:text-rose-900 mb-1 transition-colors duration-300">6</p>
            <p className="text-sm font-semibold text-gray-600 group-hover:text-rose-800 mb-2 transition-colors duration-300">Mentors & Partners</p>
            <div className="flex items-center text-xs text-rose-600 font-medium group-hover:text-rose-700">
              <span>View network</span>
              <ArrowRight className="h-3 w-3 ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>

        {/* 6 Pillars Performance */}
        <div className="bg-white rounded-xl p-8 shadow-xl border-2 border-gray-200">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
            <Target className="h-8 w-8 mr-3 text-indigo-600" />
            6 Pillars Performance
          </h2>
          <div className="space-y-5">
            {pillarData.map((pillar) => (
              <div key={pillar.id} className="group cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-all duration-300">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-base font-semibold text-gray-800 group-hover:text-indigo-700 transition-colors">{pillar.name}</span>
                  <span className="text-base font-bold text-gray-900 group-hover:text-indigo-800 transition-colors">{pillar.score}/100</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4 group-hover:h-5 transition-all duration-300">
                  <div 
                    className={`bg-gradient-to-r ${pillar.color} h-4 group-hover:h-5 rounded-full transition-all duration-500 shadow-md group-hover:shadow-lg`}
                    style={{ width: `${pillar.score}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Journey Progress & Recognition */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-8 shadow-xl border-2 border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <CheckCircle className="h-7 w-7 mr-3 text-green-600" />
              Journey Progress
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                <span className="text-gray-800 font-medium flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                  True Profile Complete
                </span>
                <span className="text-green-700 font-bold">100%</span>
              </div>
              <LearningProgressTile onOpen={() => setActiveModule && setActiveModule('pillars')} />
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                <span className="text-gray-800 font-medium flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                  Assessment Complete
                </span>
                <span className="text-green-700 font-bold">Done</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                <span className="text-gray-800 font-medium flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                  Best Practices Shared
                </span>
                <span className="text-green-700 font-bold">4</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                <span className="text-gray-800 font-medium flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                  Directory Listed
                </span>
                <span className="text-green-700 font-bold">Yes</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-xl border-2 border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Award className="h-7 w-7 mr-3 text-orange-600" />
              Recognition & Impact
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
                <span className="text-gray-800 font-medium">Certification Level</span>
                <span className="text-orange-600 font-bold text-lg">{currentOrgData.certification_level}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                <span className="text-gray-800 font-medium">CIS Score</span>
                <span className="text-blue-600 font-bold text-lg">{currentOrgData.certification_score * 10}/1000</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                <span className="text-gray-800 font-medium">Community Rank</span>
                <span className="text-green-600 font-bold text-lg">Top 30%</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg border border-purple-200">
                <span className="text-gray-800 font-medium">Practices Published</span>
                <span className="text-purple-600 font-bold text-lg">4 Active</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-rose-50 to-pink-50 rounded-lg border border-rose-200">
                <span className="text-gray-800 font-medium">Network Connections</span>
                <span className="text-rose-600 font-bold text-lg">6 Partners</span>
              </div>
            </div>
          </div>
        </div>

        {/* Published Best Practices Showcase */}
        <div className="bg-white rounded-xl p-8 shadow-xl border-2 border-gray-200">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
            <Lightbulb className="h-8 w-8 mr-3 text-amber-600" />
            Published Best Practices
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {bestPractices.map((practice) => (
              <div key={practice.id} className="group border-2 border-amber-200 hover:border-amber-300 rounded-xl p-6 hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 cursor-pointer hover:-translate-y-1">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h4 className="text-xl font-bold text-gray-900 group-hover:text-amber-900 mb-2 transition-colors">{practice.title}</h4>
                    <span className="inline-block px-3 py-1 bg-amber-200 group-hover:bg-amber-300 text-amber-900 rounded-full text-sm font-semibold transition-colors">
                      {practice.category}
                    </span>
                  </div>
                  <div className="text-right ml-4">
                    <div className="flex items-center space-x-2 text-rose-600 group-hover:text-rose-700 mb-2 transition-colors">
                      <Heart className="h-5 w-5 fill-current group-hover:scale-110 transition-transform" />
                      <span className="font-bold text-lg">{practice.likes}</span>
                    </div>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold transition-all ${
                      practice.impact === 'Very High' ? 'bg-green-100 group-hover:bg-green-200 text-green-800' : 'bg-blue-100 group-hover:bg-blue-200 text-blue-800'
                    }`}>
                      {practice.impact} Impact
                    </span>
                  </div>
                </div>
                <p className="text-gray-700 group-hover:text-gray-900 leading-relaxed transition-colors">{practice.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Team Highlights */}
        <div className="bg-white rounded-xl p-8 shadow-xl border-2 border-gray-200">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
            <Users className="h-8 w-8 mr-3 text-emerald-600" />
            Team Highlights
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="group bg-gradient-to-br from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 rounded-xl p-6 border-2 border-emerald-200 hover:border-emerald-300 text-center cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
              <div className="text-5xl font-bold text-emerald-600 group-hover:text-emerald-700 mb-2 transition-colors">{teamMembers.length}</div>
              <p className="text-gray-700 group-hover:text-emerald-900 font-semibold transition-colors">Total Team Members</p>
              <p className="text-sm text-emerald-700 group-hover:text-emerald-800 mt-2 transition-colors">{teamMembers.filter(m => m.status === 'active').length} Active</p>
            </div>
            <div className="group bg-gradient-to-br from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 rounded-xl p-6 border-2 border-blue-200 hover:border-blue-300 text-center cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
              <div className="text-5xl font-bold text-blue-600 group-hover:text-blue-700 mb-2 transition-colors">8</div>
              <p className="text-gray-700 group-hover:text-blue-900 font-semibold transition-colors">Departments</p>
              <p className="text-sm text-blue-700 group-hover:text-blue-800 mt-2 transition-colors">Cross-functional</p>
            </div>
            <div className="group bg-gradient-to-br from-purple-50 to-violet-50 hover:from-purple-100 hover:to-violet-100 rounded-xl p-6 border-2 border-purple-200 hover:border-purple-300 text-center cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
              <div className="text-5xl font-bold text-purple-600 group-hover:text-purple-700 mb-2 transition-colors">98%</div>
              <p className="text-gray-700 group-hover:text-purple-900 font-semibold transition-colors">Employee Satisfaction</p>
              <p className="text-sm text-purple-700 group-hover:text-purple-800 mt-2 transition-colors">Latest Survey</p>
            </div>
          </div>
        </div>

        {showModal === 'cis' && <CISScoreModal />}
        {showModal === 'team' && <TeamMembersModal />}
        {showModal === 'practices' && <BestPracticesModal />}
        {showModal === 'mentors' && <MentorPartnerModal />}
        {showModal === 'share' && <ShareModal />}
        {showModal === 'downloadCert' && <DownloadCertModal />}
        {showModal === 'downloadBadge' && <DownloadBadgeModal />}
        {showModal === 'downloadScores' && <DownloadScoresModal />}
        {showElevationModal && <ElevationModal />}
        
        {/* True Profile Prompt Modal */}
        {showTrueProfilePrompt && (
          <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowTrueProfilePrompt(false)}>
            <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-2xl">
                <div className="flex items-center space-x-3">
                  <AlertTriangle className="h-8 w-8" />
                  <h2 className="text-xl font-bold">Complete Your True Profile First</h2>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-6">
                  Before starting your CNESS assessment, please complete your organization's True Profile. This includes:
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center text-gray-700">
                    <CheckCircle2 className="h-5 w-5 text-indigo-600 mr-3" />
                    <span>Mission Statement</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <CheckCircle2 className="h-5 w-5 text-indigo-600 mr-3" />
                    <span>Vision Statement</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <CheckCircle2 className="h-5 w-5 text-indigo-600 mr-3" />
                    <span>Core Values</span>
                  </li>
                </ul>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowTrueProfilePrompt(false);
                      setActiveModule && setActiveModule('true-profile');
                    }}
                    className="flex-1 bg-indigo-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-all flex items-center justify-center space-x-2"
                  >
                    <User className="h-5 w-5" />
                    <span>Go to True Profile</span>
                  </button>
                  <button
                    onClick={() => setShowTrueProfilePrompt(false)}
                    className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Module icons
  const moduleIcons = {
    trueProfile: 'https://images.unsplash.com/photo-1730804518415-75297e8d2a41',
    assessment: 'https://images.pexels.com/photos/159298/gears-cogs-machine-machinery-159298.jpeg',
    learning: 'https://images.unsplash.com/photo-1759678444893-9c1762e022fd',
    bestPractices: 'https://images.unsplash.com/photo-1617706111157-5f5c6443a04d',
    directory: 'https://images.unsplash.com/photo-1591040092219-081fb773589c',
    mentor: 'https://images.unsplash.com/photo-1650978810641-6610f4b6d15a'
  };

  // Example Dashboard (for uncertified orgs clicking "View Example")
  // Shows the NEW certified dashboard design with example data
  const ExampleDashboard = () => {
    // Create example organization data matching the new certified dashboard structure
    const exampleData = {
      name: "Green Innovation Enterprises",
      certification_level: "Luminary",
      certification_score: 91,
      certification_date: "2025-01-15",
      logo_url: null
    };

    return (
      <div className="space-y-6">
        {/* Sticky Header - Example Dashboard Banner */}
        <div className="sticky top-0 z-50 bg-white border-b-2 border-gray-200 shadow-lg" style={{ position: 'sticky', top: 0 }}>
          <div className="bg-gradient-to-r from-orange-400 via-yellow-400 to-orange-400 text-white p-4">
            <div className="flex items-center justify-between max-w-7xl mx-auto">
              <div className="flex items-center space-x-3">
                <Eye className="h-6 w-6" />
                <h2 className="text-xl font-bold">EXAMPLE DASHBOARD - See How Your Dashboard Will Look</h2>
              </div>
              <button
                onClick={() => setShowExampleDashboard(false)}
                className="bg-white text-orange-600 px-6 py-2 rounded-lg font-semibold hover:bg-orange-50 transition-all flex items-center space-x-2"
              >
                <ArrowRight className="h-5 w-5 transform rotate-180" />
                <span>Back to My Dashboard</span>
              </button>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg p-8 border-2 border-orange-200 shadow-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_biz-assessment-1/artifacts/0e2nlbcy_Badge%20Level%202.png"
                alt="Badge"
                className="h-28 w-28 rounded-full shadow-lg"
              />
              <div>
                <h1 className="text-4xl font-bold text-gray-900">{exampleOrgData.name}</h1>
                <p className="text-2xl font-semibold text-orange-600">{exampleOrgData.certification_level} Certified</p>
                <span className="px-4 py-2 bg-orange-100 text-orange-800 rounded-full text-sm font-bold mt-3 inline-block">
                  CIS: {exampleOrgData.certification_score * 10}/1000
                </span>
              </div>
            </div>
            <button onClick={() => setShowElevationModal(true)} className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-8 py-4 rounded-xl font-bold text-lg">
              <Crown className="h-6 w-6 inline mr-2" />
              Elevate to Luminary
            </button>
          </div>
        </div>

        {/* 4 Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div onClick={() => setShowModal('cis')} className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-6 text-white shadow-xl cursor-pointer hover:scale-105 transition-all">
            <Award className="h-10 w-10 mb-3" />
            <p className="text-4xl font-bold">{exampleOrgData.certification_score * 10}</p>
            <p className="text-indigo-100 text-lg">CIS Score</p>
            <p className="text-xs text-indigo-200 mt-2">Click →</p>
          </div>
          <div onClick={() => setShowModal('team')} className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-6 text-white shadow-xl cursor-pointer hover:scale-105 transition-all">
            <Users className="h-10 w-10 mb-3" />
            <p className="text-4xl font-bold">12</p>
            <p className="text-emerald-100 text-lg">Team</p>
            <p className="text-xs text-emerald-200 mt-2">Click →</p>
          </div>
          <div onClick={() => setShowModal('practices')} className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-6 text-white shadow-xl cursor-pointer hover:scale-105 transition-all">
            <Lightbulb className="h-10 w-10 mb-3" />
            <p className="text-4xl font-bold">4</p>
            <p className="text-amber-100 text-lg">Best Practices</p>
            <p className="text-xs text-amber-200 mt-2">Click →</p>
          </div>
          <div onClick={() => setShowModal('mentors')} className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl p-6 text-white shadow-xl cursor-pointer hover:scale-105 transition-all">
            <Heart className="h-10 w-10 mb-3" />
            <p className="text-4xl font-bold">6</p>
            <p className="text-rose-100 text-lg">Mentors</p>
            <p className="text-xs text-rose-200 mt-2">Click →</p>
          </div>
        </div>

        {/* 6 Pillars */}
        <div className="bg-white rounded-xl p-8 shadow-xl border-2 border-gray-200">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">6 Pillars Performance</h2>
          <div className="space-y-5">
            {pillarData.map((pillar) => (
              <div key={pillar.id}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-base font-semibold text-gray-800">{pillar.name}</span>
                  <span className="text-base font-bold text-gray-900">{pillar.score}/100</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div className={`bg-gradient-to-r ${pillar.color} h-4 rounded-full`} style={{ width: `${pillar.score}%` }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* More sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-8 shadow-xl border-2 border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Journey Progress</h3>
            <div className="space-y-4">
              {['True Profile', '6 Pillars Learning', 'Assessment Complete', 'Best Practices Shared', 'Directory Listed'].map((item, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                  <span className="text-gray-800 font-medium flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                    {item}
                  </span>
                  <span className="text-green-700 font-bold">✓</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-xl border-2 border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Recognition</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                <span className="text-gray-800 font-medium">Certification</span>
                <span className="text-orange-600 font-bold text-lg">{exampleOrgData.certification_level}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <span className="text-gray-800 font-medium">CIS Score</span>
                <span className="text-blue-600 font-bold text-lg">{exampleOrgData.certification_score * 10}/1000</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // My Dashboard - Shows either action cards OR rich dashboard based on certification status
  const MyDashboard = () => {
    // CERTIFIED ORG - Show rich dashboard with THEIR data
    if (organizationData?.certification_level) {
      const getBadgeUrl = () => {
        if (organizationData.certification_level === 'Luminary') {
          return 'https://customer-assets.emergentagent.com/job_biz-assessment-1/artifacts/k1c5ivh1_Badge%20Level%203.png';
        } else if (organizationData.certification_level === 'Inspired') {
          return 'https://customer-assets.emergentagent.com/job_biz-assessment-1/artifacts/0e2nlbcy_Badge%20Level%202.png';
        } else {
          return 'https://customer-assets.emergentagent.com/job_biz-assessment-1/artifacts/3z5bcj72_Badge%20Level%201.png';
        }
      };
      const badgeUrl = getBadgeUrl();

      return (
        <div className="space-y-6">
          {/* STICKY HEADER - NO EXAMPLE BANNER FOR CERTIFIED ORGS */}
          <div className="sticky top-0 z-40 bg-white shadow-xl border-b-4 border-amber-400" style={{ position: 'sticky', top: 0 }}>
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-6">
              <div className="max-w-7xl mx-auto flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <img src={badgeUrl} alt="Badge" className="h-20 w-20 rounded-full shadow-lg" />
                    <div className="absolute -top-1 -right-1 bg-orange-500 rounded-full p-1.5">
                      <Star className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">{organizationData.name}</h1>
                    <p className="text-xl font-semibold text-orange-600">{organizationData.certification_level} Certified</p>
                    <div className="flex items-center space-x-3 mt-2">
                      <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-bold">
                        CIS: {organizationData.certification_score * 10}/1000
                      </span>
                      <span className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm font-bold">
                        {organizationData.employee_count || 120} Employees
                      </span>
                    </div>
                  </div>
                </div>
                {organizationData.certification_level !== 'Luminary' && (
                  <button onClick={() => setShowElevationModal(true)} className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-6 py-3 rounded-xl hover:shadow-2xl transition-all flex items-center space-x-2 font-bold">
                    <Crown className="h-6 w-6" />
                    <span>Elevate to Luminary</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* 4 ELEGANT CARDS */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div onClick={() => setShowModal('cis')} className="group bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg p-4 text-white shadow-lg cursor-pointer hover:shadow-2xl transition-all">
              <div className="flex items-center justify-between mb-2">
                <Award className="h-8 w-8" />
                <span className="text-xs bg-white/20 px-2 py-1 rounded-full">View Details</span>
              </div>
              <p className="text-3xl font-bold mb-1">{organizationData.certification_score * 10}</p>
              <p className="text-indigo-100 text-sm font-medium">CIS Score</p>
            </div>
            <div onClick={() => setShowModal('team')} className="group bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg p-4 text-white shadow-lg cursor-pointer hover:shadow-2xl transition-all">
              <div className="flex items-center justify-between mb-2">
                <Users className="h-8 w-8" />
                <span className="text-xs bg-white/20 px-2 py-1 rounded-full">View Team</span>
              </div>
              <p className="text-3xl font-bold mb-1">12</p>
              <p className="text-emerald-100 text-sm font-medium">Team Members</p>
            </div>
            <div onClick={() => setShowModal('practices')} className="group bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg p-4 text-white shadow-lg cursor-pointer hover:shadow-2xl transition-all">
              <div className="flex items-center justify-between mb-2">
                <Lightbulb className="h-8 w-8" />
                <span className="text-xs bg-white/20 px-2 py-1 rounded-full">View All</span>
              </div>
              <p className="text-3xl font-bold mb-1">4</p>
              <p className="text-amber-100 text-sm font-medium">Best Practices</p>
            </div>
            <div onClick={() => setShowModal('mentors')} className="group bg-gradient-to-br from-rose-500 to-pink-600 rounded-lg p-4 text-white shadow-lg cursor-pointer hover:shadow-2xl transition-all">
              <div className="flex items-center justify-between mb-2">
                <Heart className="h-8 w-8" />
                <span className="text-xs bg-white/20 px-2 py-1 rounded-full">View Network</span>
              </div>
              <p className="text-3xl font-bold mb-1">6</p>
              <p className="text-rose-100 text-sm font-medium">Mentors & Partners</p>
            </div>
          </div>

          {/* 6 PILLARS PERFORMANCE */}
          <div className="bg-white rounded-xl p-8 shadow-xl border-2 border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
              <Target className="h-8 w-8 mr-3 text-indigo-600" />
              6 Pillars Performance
            </h2>
            <div className="space-y-5">
              {pillarData.map((pillar) => (
                <div key={pillar.id}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-base font-semibold text-gray-800">{pillar.name}</span>
                    <span className="text-base font-bold text-gray-900">{pillar.score}/100</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div className={`bg-gradient-to-r ${pillar.color} h-4 rounded-full transition-all duration-500 shadow-md`} style={{ width: `${pillar.score}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* JOURNEY PROGRESS & RECOGNITION */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-8 shadow-xl border-2 border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <CheckCircle className="h-7 w-7 mr-3 text-green-600" />
                Journey Progress
              </h3>
              <div className="space-y-4">
                {['True Profile Complete', '6 Pillars Learning', 'Assessment Complete', 'Best Practices Shared', 'Directory Listed'].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                    <span className="text-gray-800 font-medium flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                      {item}
                    </span>
                    <span className="text-green-700 font-bold">100%</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-xl border-2 border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Award className="h-7 w-7 mr-3 text-orange-600" />
                Recognition & Impact
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
                  <span className="text-gray-800 font-medium">Certification Level</span>
                  <span className="text-orange-600 font-bold text-lg">{organizationData.certification_level}</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                  <span className="text-gray-800 font-medium">CIS Score</span>
                  <span className="text-blue-600 font-bold text-lg">{organizationData.certification_score * 10}/1000</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                  <span className="text-gray-800 font-medium">Community Rank</span>
                  <span className="text-green-600 font-bold text-lg">Top 30%</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg border border-purple-200">
                  <span className="text-gray-800 font-medium">Practices Published</span>
                  <span className="text-purple-600 font-bold text-lg">4 Active</span>
                </div>
              </div>
            </div>
          </div>

          {/* BEST PRACTICES SHOWCASE */}
          <div className="bg-white rounded-xl p-8 shadow-xl border-2 border-gray-200">
            <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
              <Lightbulb className="h-8 w-8 mr-3 text-amber-600" />
              Published Best Practices
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {bestPractices.map((practice) => (
                <div key={practice.id} className="border-2 border-amber-200 rounded-xl p-6 hover:shadow-2xl transition-all bg-gradient-to-br from-amber-50 to-orange-50">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-gray-900 mb-2">{practice.title}</h4>
                      <span className="inline-block px-3 py-1 bg-amber-200 text-amber-900 rounded-full text-sm font-semibold">
                        {practice.category}
                      </span>
                    </div>
                    <div className="text-right ml-4">
                      <div className="flex items-center space-x-2 text-rose-600 mb-2">
                        <Heart className="h-5 w-5 fill-current" />
                        <span className="font-bold text-lg">{practice.likes}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{practice.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* TEAM HIGHLIGHTS */}
          <div className="bg-white rounded-xl p-8 shadow-xl border-2 border-gray-200">
            <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
              <Users className="h-8 w-8 mr-3 text-emerald-600" />
              Team Highlights
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border-2 border-emerald-200 text-center">
                <div className="text-5xl font-bold text-emerald-600 mb-2">12</div>
                <p className="text-gray-700 font-semibold">Total Team Members</p>
                <p className="text-sm text-emerald-700 mt-2">11 Active</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200 text-center">
                <div className="text-5xl font-bold text-blue-600 mb-2">8</div>
                <p className="text-gray-700 font-semibold">Departments</p>
                <p className="text-sm text-blue-700 mt-2">Cross-functional</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6 border-2 border-purple-200 text-center">
                <div className="text-5xl font-bold text-purple-600 mb-2">98%</div>
                <p className="text-gray-700 font-semibold">Employee Satisfaction</p>
                <p className="text-sm text-purple-700 mt-2">Latest Survey</p>
              </div>
            </div>
          </div>

          {/* MODALS */}
          {showModal === 'cis' && <CISScoreModal />}
          {showModal === 'team' && <TeamMembersModal />}
          {showModal === 'practices' && <BestPracticesModal />}
          {showModal === 'mentors' && <MentorPartnerModal />}
          {showModal === 'share' && <ShareModal />}
          {showModal === 'downloadCert' && <DownloadCertModal />}
          {showModal === 'downloadBadge' && <DownloadBadgeModal />}
          {showModal === 'downloadScores' && <DownloadScoresModal />}
          {showElevationModal && <ElevationModal />}
        </div>
      );
    }

    // UNCERTIFIED ORG - Show action cards
    return (
      <div className="space-y-6">
        {/* Sticky Header */}
        <div className="sticky top-0 z-50 bg-white border-b-2 border-gray-200 shadow-lg" style={{ position: 'sticky', top: 0 }}>
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4">
            <div className="flex items-center justify-between max-w-7xl mx-auto">
              <div>
                <h1 className="text-2xl font-bold">{organizationData?.name || 'Welcome to CNESS'}</h1>
                <p className="text-indigo-100">Start your certification journey</p>
              </div>
              <button
                onClick={() => setShowExampleDashboard(true)}
                className="bg-white text-indigo-600 px-6 py-2 rounded-lg font-semibold hover:bg-indigo-50 transition-all flex items-center space-x-2"
              >
                <Eye className="h-5 w-5" />
                <span>View Example Dashboard</span>
              </button>
            </div>
          </div>
        </div>

      {/* Journey Progress */}
      <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Your Certification Journey</h2>
        <div className="flex items-center justify-between">
          <div className="text-center">
            <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-indigo-600 font-bold">1</span>
            </div>
            <p className="text-xs text-gray-600">True Profile</p>
          </div>
          <div className="flex-1 h-1 bg-gray-200 mx-2"></div>
          <div className="text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-gray-400 font-bold">2</span>
            </div>
            <p className="text-xs text-gray-600">Learning</p>
          </div>
          <div className="flex-1 h-1 bg-gray-200 mx-2"></div>
          <div className="text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-gray-400 font-bold">3</span>
            </div>
            <p className="text-xs text-gray-600">Assessment</p>
          </div>
          <div className="flex-1 h-1 bg-gray-200 mx-2"></div>
          <div className="text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-gray-400 font-bold">4</span>
            </div>
            <p className="text-xs text-gray-600">Certification</p>
          </div>
        </div>
      </div>

      {/* Six-Pillar Radar */}
      <PillarRadarCard
        organizationId={organizationId}
        onStartAssessment={() => setActiveModule && setActiveModule('assessment')}
      />

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div 
          onClick={() => setActiveModule && setActiveModule('true-profile')}
          className="group bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-6 text-white cursor-pointer hover:scale-105 transition-all shadow-xl overflow-hidden relative"
        >
          <div className="absolute top-0 right-0 w-32 h-32 opacity-20">
            <img src={moduleIcons.trueProfile} alt="" className="w-full h-full object-cover" />
          </div>
          <User className="h-10 w-10 mb-4 relative z-10" />
          <h3 className="text-xl font-bold mb-2 relative z-10">True Profile</h3>
          <p className="text-indigo-100 mb-4 text-sm relative z-10">Define your identity</p>
          <div className="flex items-center text-sm relative z-10">
            <ArrowRight className="h-4 w-4 mr-2" />
            <span>Start Here</span>
          </div>
        </div>

        <div 
          onClick={handleAssessmentClick}
          className={`group bg-gradient-to-br ${assessmentCardState.color} rounded-xl p-6 text-white cursor-pointer hover:scale-105 transition-all shadow-xl overflow-hidden relative`}
          data-testid="assessment-engine-card"
        >
          <div className="absolute top-0 right-0 w-32 h-32 opacity-20">
            <img src={moduleIcons.assessment} alt="" className="w-full h-full object-cover" />
          </div>
          <Target className="h-10 w-10 mb-4 relative z-10" />
          <h3 className="text-xl font-bold mb-2 relative z-10">Assessment Engine</h3>
          <p className="text-white/80 mb-4 text-sm relative z-10">{assessmentCardState.description}</p>
          <div className="flex items-center text-sm relative z-10">
            <ArrowRight className="h-4 w-4 mr-2" />
            <span>{assessmentCardState.label}</span>
          </div>
        </div>

        <div 
          onClick={() => setActiveModule && setActiveModule('pillars')}
          data-testid="dashboard-learning-tile"
          className="group bg-gradient-to-br from-violet-500 to-fuchsia-600 rounded-xl p-6 text-white cursor-pointer hover:scale-105 transition-all shadow-xl overflow-hidden relative"
        >
          <div className="absolute top-0 right-0 w-32 h-32 opacity-20">
            <img src={moduleIcons.learning} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex items-start justify-between relative z-10">
            <BookOpen className="h-10 w-10 mb-4" />
            <span className="text-2xl font-bold" data-testid="dashboard-learning-count">
              {learningSummary ? `${learningSummary.courses_completed}/${learningSummary.courses_total}` : '—'}
            </span>
          </div>
          <h3 className="text-xl font-bold mb-2 relative z-10">6 Pillars Learning</h3>
          <div className="h-1.5 w-full bg-white/25 rounded-full mb-3 relative z-10 overflow-hidden">
            <div className="h-full bg-white rounded-full transition-all duration-700" style={{ width: `${learningSummary?.percent || 0}%` }} />
          </div>
          <p className="text-violet-100 mb-4 text-sm relative z-10" data-testid="dashboard-learning-next">
            {learningSummary?.next_course
              ? `Next: Pillar ${learningSummary.next_course.pillar_number} · ${learningSummary.next_course.title}`
              : learningSummary ? 'All six pillars complete' : 'Master the six pillars'}
          </p>
          <div className="flex items-center text-sm relative z-10">
            <ArrowRight className="h-4 w-4 mr-2" />
            <span>{learningSummary?.courses_completed ? 'Continue learning' : 'Start learning'}</span>
          </div>
        </div>

        <div 
          onClick={() => setActiveModule && setActiveModule('best-practices')}
          className="group bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-6 text-white cursor-pointer hover:scale-105 transition-all shadow-xl overflow-hidden relative"
        >
          <div className="absolute top-0 right-0 w-32 h-32 opacity-20">
            <img src={moduleIcons.bestPractices} alt="" className="w-full h-full object-cover" />
          </div>
          <Lightbulb className="h-10 w-10 mb-4 relative z-10" />
          <h3 className="text-xl font-bold mb-2 relative z-10">Best Practices Hub</h3>
          <p className="text-amber-100 mb-4 text-sm relative z-10">Share insights</p>
          <div className="flex items-center text-sm relative z-10">
            <ArrowRight className="h-4 w-4 mr-2" />
            <span>Explore</span>
          </div>
        </div>

        <div 
          onClick={() => setActiveModule && setActiveModule('directory')}
          className="group bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl p-6 text-white cursor-pointer hover:scale-105 transition-all shadow-xl overflow-hidden relative"
        >
          <div className="absolute top-0 right-0 w-32 h-32 opacity-20">
            <img src={moduleIcons.directory} alt="" className="w-full h-full object-cover" />
          </div>
          <Globe className="h-10 w-10 mb-4 relative z-10" />
          <h3 className="text-xl font-bold mb-2 relative z-10">Recognition Directory</h3>
          <p className="text-cyan-100 mb-4 text-sm relative z-10">Browse certified orgs</p>
          <div className="flex items-center text-sm relative z-10">
            <ArrowRight className="h-4 w-4 mr-2" />
            <span>View Directory</span>
          </div>
        </div>

        <div 
          onClick={() => setActiveModule && setActiveModule('mentor-partner')}
          className="group bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl p-6 text-white cursor-pointer hover:scale-105 transition-all shadow-xl overflow-hidden relative"
        >
          <div className="absolute top-0 right-0 w-32 h-32 opacity-20">
            <img src={moduleIcons.mentor} alt="" className="w-full h-full object-cover" />
          </div>
          <Users className="h-10 w-10 mb-4 relative z-10" />
          <h3 className="text-xl font-bold mb-2 relative z-10">Mentor & Partner</h3>
          <p className="text-rose-100 mb-4 text-sm relative z-10">Connect with community</p>
          <div className="flex items-center text-sm relative z-10">
            <ArrowRight className="h-4 w-4 mr-2" />
            <span>Get Connected</span>
          </div>
        </div>
      </div>
      </div>
    );
  };

  // For Example Dashboard, temporarily use example data with CertifiedOrgDashboard
  const displayData = showExampleDashboard ? {
    name: "Green Innovation Enterprises",
    certification_level: "Luminary",
    certification_score: 91,
    certification_date: "2025-01-15",
    logo_url: "https://customer-assets.emergentagent.com/job_927ed11e-f95b-4f37-8ab1-d65489a73b20/artifacts/t56xufu9_Badge%20Level%203.png"
  } : organizationData;

  return (
    <div>
      {showExampleDashboard && (
        <div className="sticky top-0 z-50 bg-gradient-to-r from-orange-400 via-yellow-400 to-orange-400 text-white p-4 shadow-lg mb-6">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center space-x-3">
              <Eye className="h-6 w-6" />
              <h2 className="text-xl font-bold">EXAMPLE DASHBOARD - This is how YOUR dashboard will look after certification</h2>
            </div>
            <button
              onClick={() => setShowExampleDashboard(false)}
              className="bg-white text-orange-600 px-6 py-2 rounded-lg font-semibold hover:bg-orange-50 transition-all flex items-center space-x-2"
            >
              <ArrowRight className="h-5 w-5 transform rotate-180" />
              <span>Back to My Dashboard</span>
            </button>
          </div>
        </div>
      )}
      
      {(displayData?.certification_level || showExampleDashboard) ? (
        <CertifiedOrgDashboard />
      ) : (
        <MyDashboard />
      )}
    </div>
  );
};

export default OrgAdminDashboard;